import type { FC, ReactNode } from 'react'
import type { QaItem } from '@/types'

import Link from 'next/link'
import { ArrowRight, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import SourceLine from '@/components/kb/source-line'

interface QaCardProps {
    qa: QaItem
    defaultOpen?: boolean
    showSource?: boolean
    className?: string
}

const QaCard: FC<QaCardProps> = ({ qa, defaultOpen = false, showSource = true, className }): ReactNode => {
    return (
        <details
            className={cn('group rounded-xl border-2 border-foreground bg-card shadow-brutal-sm transition-shadow open:shadow-brutal', className)}
            open={defaultOpen}
        >
            <summary className='flex cursor-pointer items-start justify-between gap-4 rounded-xl p-4 text-left font-semibold leading-snug hover:bg-secondary/60'>
                <span data-mdlfy-cms={`questions:${qa.id}:question`}>
                    {qa.question}
                </span>

                <span className='flex size-7 shrink-0 items-center justify-center rounded-md border-2 border-foreground bg-primary transition-transform group-open:rotate-45'>
                    <Plus className='size-4' aria-hidden='true' />
                </span>
            </summary>

            <div className='border-t-2 border-foreground px-4 pb-4 pt-3'>
                <p className='whitespace-pre-line text-sm leading-relaxed' data-mdlfy-cms={`questions:${qa.id}:answer`}>
                    {qa.answer}
                </p>

                <div className='mt-4 flex flex-wrap items-center justify-between gap-3'>
                    {showSource ? <SourceLine source={qa} /> : <span />}

                    <Link href={`/qa/${qa.slug}`} className='inline-flex items-center gap-1 text-xs font-bold underline-offset-4 hover:underline'>
                        Open answer
                        <ArrowRight className='size-4' aria-hidden='true' />
                    </Link>
                </div>
            </div>
        </details>
    )
}

export default QaCard
